import { Eye } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { NodeProps } from "reactflow";
import { useGetFacilityWholeList } from "../../../../../api/cultivations";
import TailwindSideModal from "../../../../../components/commonModal/TailwindSideModal";
import { isNodeLabelExists } from "../../../../../components/drawing-tool/utils";
import TailwindBottomButtonView from "../../../../../components/mainBottomButtonView/TailwindBottomButtonView";
import { Button } from "../../../../../components/ui/common/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../../components/ui/common/card";
import { Form } from "../../../../../components/ui/common/form";
import { FormInput } from "../../../../../components/ui/Input/FormInput/FormInput";
import { FormSearchSelect } from "../../../../../components/ui/Input/FormSearchSelect/FormSearchSelect";
import CommonSplitContainer from "../../../../../components/ui/layout/CommonSplitContainer/CommonSplitContainer";
import { NEW_FACILITY_KEY, NewFacilityOption } from "../../../../../config/constants";
import { FacilityType } from "../../../../../config/enums/FacilityType.enum";
import { NodeLabelExistError } from "../../../../../config/enums/NodeLabelError.enum";
import { errorView } from "../../../../../helpers/ErrorHandler";
import FacilityRegisterModal from "../../../../facility/facilityRegister/FacilityRegisterModal";
import ManageFacilityData from "../../../../facility/ManageFacilityData";

interface ProcessGateFormModalProps {
  setProcessGateNode: (node: NodeProps | null, isCreated: boolean) => void;
  newNode?: any;
  edit?: boolean;
  processGateName?: any;
  updateProcessGate: (processGateName: string, newNode: any) => void;
  createProcessGate: (processGateName: string, newNode: any) => void;
  chartNodes: any[];
}

const ProcessGateFormModal: React.FC<ProcessGateFormModalProps> = ({
  setProcessGateNode,
  newNode,
  edit,
  processGateName,
  updateProcessGate,
  createProcessGate,
  chartNodes
}) => {
  const [facilityId, setFacilityId] = useState("");
  const [visibleFacilityManager, setVisibleFacilityManager] = useState<boolean>(false);
  const [showFacilityRegister, setShowFacilityRegister] = useState<boolean>(false);

  const {
    data: facilityWholeList,
    isSuccess: isFacilityDataSuccess,
    refetch: refetchFacilityList
  } = useGetFacilityWholeList(FacilityType.FACTORY);

  const facilityListData = useMemo(() => {
    if (isFacilityDataSuccess) {
      if (facilityWholeList?.length > 0) {
        return [NewFacilityOption, ...facilityWholeList];
      }
      return [NewFacilityOption];
    }
    return [];
  }, [facilityWholeList, isFacilityDataSuccess]);

  // FormSearchSelect options use { id, value, title } rather than the semantic { key, value, text }.
  const facilityOptions = useMemo(
    () =>
      facilityListData.map((facility: any) => ({
        id: facility.value,
        value: facility.value,
        title: facility.text
      })),
    [facilityListData]
  );

  const defaultValues = useMemo(() => {
    return {
      processGateName: edit && processGateName ? processGateName : "",
      facilityName: edit && newNode?.data?.facilityId ? newNode.data.facilityId : ""
    };
  }, [edit, processGateName, newNode]);

  const methods = useForm({
    mode: "all",
    defaultValues
  });

  const { control, handleSubmit, setValue, reset, clearErrors, setError, setFocus } = methods;

  const handleFacilityChange = (value: string) => {
    if (value === NEW_FACILITY_KEY) {
      // FormSearchSelect has already written the option into the field — put the previous
      // selection back so "+ New Facility" is never kept as the chosen facility.
      setValue("facilityName", facilityId);
      setShowFacilityRegister(true);
      return;
    }
    setFacilityId(value);
    setValue("facilityName", value);
  };

  useEffect(() => {
    reset(defaultValues);
    if (edit && newNode?.data?.facilityId) {
      setFacilityId(newNode.data.facilityId);
    } else {
      setFacilityId("");
    }
  }, [edit, newNode, processGateName, reset, defaultValues]);

  const closeModal = () => {
    setProcessGateNode(null, false);
  };

  const onSubmit = (data: { processGateName: string; facilityName: string }) => {
    if (
      isNodeLabelExists(chartNodes as any, data?.processGateName) &&
      data?.processGateName !== processGateName
    ) {
      clearErrors("processGateName");
      setFocus("processGateName");
      setError("processGateName", { type: "manual", message: NodeLabelExistError.PROCESS_GATE });
      return;
    }

    if (!facilityId) {
      return errorView("Facility name is required");
    }

    const modifiedNode = {
      ...newNode,
      data: {
        ...newNode?.data,
        label: `Process Gate (${data.processGateName})`,
        facilityId: facilityId
      }
    };

    if (edit) {
      updateProcessGate(data.processGateName, modifiedNode);
    } else {
      createProcessGate(data.processGateName, modifiedNode);
    }
  };

  return (
    <>
      <TailwindSideModal
        open
        onOpenChange={open => !open && closeModal()}
        title={edit ? "Edit Process Gate" : "Add Process Gate"}
        contentClassName="sm:!tw-max-w-3xl"
      >
        <Form {...methods}>
          <CommonSplitContainer
            bottomClassName="tw-border-t tw-border-border tw-bg-background"
            topChild={
              <form onSubmit={handleSubmit(onSubmit)} className="tw-flex tw-flex-col tw-gap-5 tw-px-4 tw-pt-4">
                <Card className="tw-rounded-lg tw-border-border tw-gap-3 tw-py-4">
                  <CardHeader className="tw-px-4">
                    <CardTitle className="tw-text-sm tw-font-semibold">
                      Process gate details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="tw-px-4">
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                      <FormInput
                        control={control}
                        name="processGateName"
                        label="Process Gate Name"
                        placeholder="Process Gate Name"
                        required
                        errorMessage="Process Gate Name is required"
                      />
                      <div className="tw-flex tw-items-end tw-gap-2">
                        <div className="tw-flex-1 tw-min-w-0">
                          <FormSearchSelect
                            control={control}
                            name="facilityName"
                            label="Facility Name"
                            placeholder="Select Facility Name"
                            options={facilityOptions}
                            required
                            loading={!isFacilityDataSuccess}
                            errorMessage="Facility name is required"
                            onChange={handleFacilityChange}
                          />
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="lg"
                          onClick={() => setVisibleFacilityManager(true)}
                          disabled={!facilityId}
                          className="tw-h-10 tw-shrink-0 tw-gap-1.5 tw-px-4 tw-border-border tw-font-medium tw-text-foreground hover:tw-bg-muted hover:tw-text-foreground"
                        >
                          <Eye className="tw-h-4 tw-w-4" />
                          View
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                {showFacilityRegister && (
                  <FacilityRegisterModal
                    visibleModal={showFacilityRegister}
                    setVisibleModal={(open: boolean) => setShowFacilityRegister(open)}
                    refetchFacilityList={refetchFacilityList}
                  />
                )}
              </form>
            }
            bottomChild={
              <TailwindBottomButtonView
                cancelButtonStatus={true}
                cancelButton={closeModal}
                saveButtonStatus={true}
                saveTitle={edit ? "Update" : "Create"}
                saveButton={handleSubmit(onSubmit)}
                customHeight={72}
                customBottomContentMain="!tw-px-4 tw-py-3"
              />
            }
          />
        </Form>
      </TailwindSideModal>
      {visibleFacilityManager && facilityId && (
        <ManageFacilityData
          facilityId={facilityId}
          closeManager={() => setVisibleFacilityManager(false)}
        />
      )}
    </>
  );
};

export default ProcessGateFormModal;
